/* eslint-disable max-lines */
import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import { useFormik } from 'formik';
import { capitalize } from 'lodash';
import { useRecoilValue } from 'recoil';
import { useQueryClient } from '@tanstack/react-query';

import { deleteWeightLossAdministration } from 'api/WeightLossAdministration';
import { createMedication, deleteMedication, upsertWeightLossSync } from 'api/Medication';

import { iaRa } from 'lib/helpers/utility';
import { useCustomMutation } from 'lib/hooks/useCustomMutation';
import { useAddendumModeContext } from 'lib/context/AddendumModeContext/AddendumModeContext';
import { useClinicalNoteContext } from 'lib/context/ClinicalNoteContext/ClinicalNoteContext';
import { useOverviewSurface } from 'lib/context/OverviewSurfaceContext/OverviewSurfaceContext';
import {
  buildAggregateNarrativeKey,
  buildAggregateNarrativeDependencies
} from 'lib/hooks/queries/aggregateNarrative/lib/buildAggregateNarrativeKey';
import {
  invalidateHealthSummaryForPatient,
  markPinnedToNote,
  removeHealthSummaryRow,
  upsertHealthSummaryRows
} from 'lib/helpers/healthSummaryCache';

import Input from 'components/shared/Input/Input';
import Modal from 'components/shared/Modal/Modal';
import Button from 'components/shared/Button/Button';
import Select from 'components/shared/Select/Select';
import Textarea from 'components/shared/Textarea/Textarea';
import Confirm from 'components/shared/Modal/Confirm/Confirm';
import { Alert, showAlert } from 'components/shared/Alert/Alert';
import RadioButton from 'components/shared/RadioButton/RadioButton';
import SearchProviders from 'components/Search/SearchProviders/SearchProviders';
import DateInputPicker from 'components/shared/DateInputPicker/DateInputPicker';
import { clinicalNote as clinicalNoteState, permissions, userState } from 'components/state';
import { useSubModal } from 'components/shared/Modal/components/SubModal/lib/SubModalContext';
import { getNdcStrengthAndUnit } from 'components/Forms/Custom/CurrentMedications/lib/ndcSearch';
import SubModalRender from 'components/shared/Modal/components/SubModal/components/SubModalRender';
import {
  getPatientPracticeOptions,
  getPrefilledPatientPractice
} from 'components/Patient/medications/lib/helpers';

import { isMedicationReadOnly } from '../../lib/aggregatedDataHelper';
import { ProviderMedicationSchema } from '../lib/providerMedicationSchema';
import { setAggregateNarrativeState } from '../../Provider/lib/aggregateNarrativeStates';
import SelectMedicationTable from '../../CurrentMedications/components/SelectMedicationTable';
import { translateMedicationToWeightLossDatav2 } from '../../Provider/WeightLoss/lib/helpers';
import {
  MEDICATION_STATUS,
  PATIENT_PORTAL_MEDICATION_READ_ONLY_MESSAGE,
  UNIT_OPTIONS,
  VERADIGM_MEDICATION_READ_ONLY_MESSAGE
} from '../lib/constants';
import {
  createObjectWithKeys,
  formatMedicationDataForUpsert,
  getMedicationDeleteContextMessage,
  injectionRoutes,
  medicationRouteOptions
} from '../lib/helpers';

import { handleIsPatientCase } from './helper';

const createMergeMedsUpdater = (updatedMeds) => {
  const updatedMap = new Map(updatedMeds.map((m) => [m.id, m]));
  return (old) => {
    const meds = old?.medicationHistory?.response?.medications;
    if (!meds) return old;
    const merged = meds.map((m) => (updatedMap.has(m.id) ? updatedMap.get(m.id) : m));
    const existingIds = new Set(meds.map((m) => m.id));
    const brandNew = updatedMeds.filter((m) => !existingIds.has(m.id));
    return {
      ...old,
      medicationHistory: {
        ...old.medicationHistory,
        response: { ...old.medicationHistory.response, medications: [...merged, ...brandNew] }
      }
    };
  };
};

const UpsertProviderMedication = ({
  showModal = false,
  hideModal = () => null,
  disabled = false,
  fromWeightLoss = false,
  showPreviewMedicationFromWhl = false,
  setShowPreviewMedicationFromWhl = () => {},
  patientId: patientIdProp = null,
  practiceOptions = [],
  isNonERXPrescription = false,
  returnValues = () => {},
  diffDosView = false,
  renderInline = false,
  asSubModal = false,
  onRequestRefill = null
}) => {
  const { openSubModal: hostSubModal } = useSubModal();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [showAdditionalFields, setShowAdditionalFields] = useState(false);

  const clinicalNote = useRecoilValue(clinicalNoteState);
  const userPermissions = useRecoilValue(permissions);
  const currentUser = useRecoilValue(userState);

  const fromOverview = useOverviewSurface();
  const { noteInactiveState, saveNoteRef, setOverviewData } = useClinicalNoteContext() || {};
  const { isAddendumMode, addendumId } = useAddendumModeContext() || {};
  const effectiveNoteLocked = (clinicalNote?.locked && !isAddendumMode) || noteInactiveState;

  const overviewUpsert = (slice, rows) => {
    if (!rows?.length || !setOverviewData) return;
    const upserted = new Set(rows.map((r) => r.id));
    setOverviewData((prev) => {
      const existing = Array.isArray(prev?.[slice]) ? prev[slice] : [];
      const filtered = existing.filter((r) => !upserted.has(r.id));
      return { ...prev, [slice]: [...rows, ...filtered] };
    });
  };

  const overviewRemove = (slice, ids) => {
    if (!ids?.length || !setOverviewData) return;
    const idSet = new Set(ids);
    setOverviewData((prev) => {
      const existing = prev?.[slice];
      if (!Array.isArray(existing)) return prev;
      return { ...prev, [slice]: existing.filter((r) => !idSet.has(r.id)) };
    });
  };

  const navigate = useNavigate();
  const location = useLocation();
  const { id: patientId, appointmentId } = useParams();

  const narrativePatientId = patientIdProp !== null ? String(patientIdProp) : patientId;

  const queryClient = useQueryClient();

  const lastShowModalRef = useRef(showModal);
  useEffect(() => {
    if (showModal?.open) lastShowModalRef.current = showModal;
  }, [showModal]);
  const activeShowModal = showModal?.open ? showModal : lastShowModalRef.current;

  const isVeradigmMedication = Boolean(activeShowModal?.data?.veradigm_id);
  const isPatientPortal = location.pathname.includes('/myportal');

  const medicationReadOnly = isMedicationReadOnly({
    canUserUpdatePatientDocuments: userPermissions?.form?.update_patient_documents,
    data: activeShowModal?.data,
    noteLocked: effectiveNoteLocked,
    fromPatientPortal: patientIdProp
  });
  const showPatientPortalReadOnlyAlert = isPatientPortal && (isVeradigmMedication || disabled);

  useEffect(() => {
    if (!showModal?.open) return;
    setShowAdditionalFields(checkIfWeNeedTheAdditionalFields(showModal?.data?.route));
  }, [showModal?.open, showModal?.data?.route]);

  const submitHandler = async (values) => {
    let clinicalNoteId = clinicalNote?.id;
    if (!clinicalNote?.id) {
      const cn = await saveNoteRef?.current?.checkPaymentBeforeSaving?.();

      clinicalNoteId = cn?.id;
    }

    if (isNonERXPrescription) {
      return returnValues(values);
    }

    let practiceId = null;
    if (patientIdProp) {
      practiceId = handleIsPatientCase({ values, patientIdProp, formik });
      if (!practiceId) return;
    }

    const copiedValues = structuredClone(values);
    const formattedMedData = formatMedicationDataForUpsert(copiedValues, fromWeightLoss);

    let result;
    if (fromWeightLoss || formattedMedData?.weight_loss_medication_id) {
      const formattedWhlData = translateMedicationToWeightLossDatav2(formattedMedData);
      const wlaId = fromWeightLoss
        ? activeShowModal?.data?.id
        : activeShowModal?.data?.weight_loss_medication_id || null;
      result = await upsertWeightLossSyncMutation.mutateAsync({
        patientId: narrativePatientId,
        appointmentId,
        clinicalNoteId,
        medication: { medications: [formattedMedData] },
        response: { row: [formattedWhlData] },
        formId: wlaId,
        ...(addendumId ? { addendumId } : {})
      });
    } else {
      result = await upsertFormFunctionMutation.mutateAsync({
        patientId: narrativePatientId,
        clinicalNoteId,
        appointmentId,
        medication: { medications: [formattedMedData] },
        ...(patientIdProp && { practice_id: practiceId }),
        ...(addendumId ? { addendumId } : {})
      });
    }

    if (result?.code === 0) {
      hideModal();
    }
  };

  const blankValues = () =>
    createObjectWithKeys(
      undefined,
      fromWeightLoss,
      showPreviewMedicationFromWhl,
      patientIdProp,
      isNonERXPrescription
    );

  const formik = useFormik({
    initialValues: createObjectWithKeys(
      activeShowModal?.data,
      fromWeightLoss,
      showPreviewMedicationFromWhl,
      patientIdProp,
      isNonERXPrescription
    ),
    enableReinitialize: true,
    validationSchema: disabled || medicationReadOnly ? null : ProviderMedicationSchema,
    onSubmit: submitHandler
  });

  // The modal stays mounted, so nothing destroys formik state on close. `enableReinitialize` can't
  // save us either: it deep-compares initialValues, and two consecutive "create" opens both derive
  // a deep-equal blank object, so resetForm() would never fire. Wipe explicitly once the exit
  // animation has finished — doing it in handleClose would blank the fields mid-slide.
  const handleAfterClose = () => {
    lastShowModalRef.current = null;
    formik.resetForm({ values: blankValues() });
    setShowAdditionalFields(false);
    setConfirmOpen(false);
    setShowPreviewMedicationFromWhl(false);
  };

  const hasSavedMedication = Boolean(formik?.values?.id || activeShowModal?.data?.id);
  const hasExistingMedication =
    hasSavedMedication || Boolean(formik?.values?.tempId || activeShowModal?.data?.tempId);
  const modalTitle = isVeradigmMedication
    ? 'View medication'
    : hasExistingMedication
      ? 'Update medication'
      : 'Create medication';

  const handleCurrentUnitChange = (unit) => {
    formik.setFieldValue('unit', unit);
  };

  const syncAggregateNarrativeIfNoKnown = async (noteId) => {
    const noKnown = queryClient.getQueryData(
      buildAggregateNarrativeKey({
        patientId: narrativePatientId,
        clinicalNoteId: noteId,
        type: 'medications',
        addendumId
      })
    )?.data?.narrative?.no_known;

    if (narrativePatientId && noKnown) {
      await setAggregateNarrativeState({
        queryClient,
        keys: buildAggregateNarrativeDependencies({
          patientId: narrativePatientId,
          clinicalNoteId: noteId,
          type: 'medications',
          addendumId
        }),
        payload: { synced: true, no_known: false, baseline_no_known: false }
      });
    }
  };

  const applyMedicationUpdates = (data, deps) => {
    const updatedMeds = iaRa(data?.newResponse?.response?.medications);
    if (updatedMeds.length && !addendumId) {
      const mergeMeds = createMergeMedsUpdater(updatedMeds);
      queryClient.setQueriesData({ queryKey: ['medication', ...deps], exact: true }, mergeMeds);
      const createdBy = {
        id: currentUser?.id,
        fullName: `${currentUser?.f_name ?? ''} ${currentUser?.l_name ?? ''}`.trim(),
        isPatient: currentUser?.id === Number(narrativePatientId)
      };
      upsertHealthSummaryRows({
        queryClient,
        patientId: narrativePatientId,
        slice: 'medications',
        rows: updatedMeds,
        decorate: (row, existing) => ({
          ...row,
          createdBy: row.createdBy ?? existing?.createdBy ?? createdBy
        })
      });
      overviewUpsert('medications', markPinnedToNote(updatedMeds));
    }
    queryClient.invalidateQueries({ queryKey: ['medication', ...deps], exact: true });
    if (!addendumId) {
      queryClient.invalidateQueries({ queryKey: ['medication', String(narrativePatientId)] });
      invalidateHealthSummaryForPatient({
        queryClient,
        patientId: narrativePatientId,
        refetchType: 'none'
      });
    }
    if (addendumId) {
      queryClient.invalidateQueries({
        queryKey: ['clinicalNoteAddendum']
      });
    }
  };

  const upsertCreationErrorAlert = (data) => {
    showAlert({
      title: 'Medication',
      message: data?.error ?? 'Medication creation failed! Please contact support.',
      color: 'danger'
    });
  };

  const upsertWeightLossSyncMutation = useCustomMutation({
    mutationFn: (data) => upsertWeightLossSync(navigate, data),
    successMessage: () => null,
    errorMessage: 'Medication creation failed! Please contact support.',
    handleResponse: upsertCreationErrorAlert,
    onSuccess: async (data, variables) => {
      const noteId = variables?.clinicalNoteId || clinicalNote?.id;
      await syncAggregateNarrativeIfNoKnown(noteId);

      const deps = [narrativePatientId, appointmentId, noteId].filter(Boolean);

      const newWla = data?.newResponse;

      // Backend creates the medication first, then links it to the new WLA via a follow-up update,
      // but the medication rows it returns are snapshotted BEFORE that link is written — so they
      // arrive without weight_loss_medication_id. Stamp the link here so the medications table knows
      // this med is already tied to a weight-loss administration (hides the "Add to weight loss"
      // action, which gates on !weight_loss_medication_id).
      if (newWla?.id && Array.isArray(data?.newResponse?.response?.medications)) {
        data.newResponse.response.medications = data.newResponse.response.medications.map((m) =>
          m.id === newWla.medication_id ? { ...m, weight_loss_medication_id: newWla.id } : m
        );
      }

      if (newWla && !addendumId) {
        queryClient.setQueriesData(
          { queryKey: ['weightLossAdministration', ...deps], exact: true },
          (old) => {
            if (!old?.data) return old;
            const existing = old.data.find((r) => r.id === newWla.id);
            return {
              ...old,
              data: existing
                ? old.data.map((r) => (r.id === newWla.id ? { ...r, ...newWla } : r))
                : [...old.data, newWla]
            };
          }
        );
        const wlaCreatedBy = {
          id: currentUser?.id,
          fullName: `${currentUser?.f_name ?? ''} ${currentUser?.l_name ?? ''}`.trim(),
          isPatient: currentUser?.id === Number(narrativePatientId)
        };
        upsertHealthSummaryRows({
          queryClient,
          patientId: narrativePatientId,
          slice: 'weightLossAdministration',
          rows: [newWla],
          decorate: (row, existing) => ({
            ...row,
            createdBy: row.createdBy ?? existing?.createdBy ?? wlaCreatedBy
          })
        });
        overviewUpsert('weightLossAdministration', markPinnedToNote([newWla]));
      }

      applyMedicationUpdates(data, deps);
      queryClient.invalidateQueries({
        queryKey: ['weightLossAdministration', ...deps],
        exact: true
      });
    }
  });

  const upsertFormFunctionMutation = useCustomMutation({
    mutationFn: (data) => createMedication(navigate, data),
    successMessage: () => null,
    errorMessage: 'Medication creation failed! Please contact support.',
    handleResponse: upsertCreationErrorAlert,
    onSuccess: async (data, variables) => {
      const noteId = variables?.clinicalNoteId || clinicalNote?.id;
      await syncAggregateNarrativeIfNoKnown(noteId);
      const deps = [narrativePatientId, appointmentId, noteId].filter(Boolean);
      applyMedicationUpdates(data, deps);
    }
  });

  const mutateDeleteMedication = useCustomMutation({
    // Delete a weight-loss row by its OWN id/version and let the backend cascade to the medication.
    // Going via the medication instead pairs its id with the weight-loss row's version, which matches
    // no PK once the two independent chains diverge.
    mutationFn: ({ clinicalNoteId }) => {
      const scope = fromOverview
        ? { fromOverview: true }
        : { clinicalNoteId, appointmentId, ...(addendumId ? { addendumId } : {}) };

      return fromWeightLoss
        ? deleteWeightLossAdministration(navigate, {
            weightLossAdministrationId: activeShowModal?.data?.id,
            version: activeShowModal?.data?.version,
            patientId: narrativePatientId,
            ...scope
          })
        : deleteMedication(navigate, {
            medicationId: activeShowModal?.data?.id,
            version: activeShowModal?.data?.version,
            patientId: narrativePatientId,
            ...scope
          });
    },
    successMessage: 'Medication deleted successfully',
    handleResponse: (data) => {
      showAlert({ title: 'Medications', message: data?.error, color: 'warning' });
    },
    onSuccess: ({ deleted }, variables) => {
      const deps = [
        narrativePatientId,
        appointmentId,
        variables.clinicalNoteId || clinicalNote?.id
      ].filter(Boolean);

      // Addendum delete is note-local (tombstone only) — mutating global/overview/health-summary
      // caches would drop it from the frozen locked note; just refresh the addendum detail.
      if (addendumId) {
        queryClient.invalidateQueries({ queryKey: ['clinicalNoteAddendum'] });
        return;
      }

      // Which side of the link the response carries depends on the endpoint called above.
      const deletedRows = iaRa(deleted);
      const deletedMedIds = new Set(
        fromWeightLoss
          ? deletedRows.map((r) => r.medication_id).filter(Boolean)
          : deletedRows.map((r) => r.id)
      );
      const linkedWlaIds = new Set(
        fromWeightLoss
          ? deletedRows.map((r) => r.id)
          : deletedRows.map((r) => r.weight_loss_medication_id).filter(Boolean)
      );
      const removeMeds = (old) => {
        const meds = old?.medicationHistory?.response?.medications;
        if (!meds) return old;
        return {
          ...old,
          medicationHistory: {
            ...old.medicationHistory,
            response: {
              ...old.medicationHistory.response,
              medications: meds.filter((m) => !deletedMedIds.has(m.id))
            }
          }
        };
      };
      queryClient.setQueriesData({ queryKey: ['medication', ...deps], exact: true }, removeMeds);

      if (linkedWlaIds.size) {
        queryClient.setQueriesData(
          { queryKey: ['weightLossAdministration', ...deps], exact: true },
          (old) => {
            if (!old?.data) return old;
            return { ...old, data: old.data.filter((r) => !linkedWlaIds.has(r.id)) };
          }
        );
      }

      deletedMedIds.forEach((id) =>
        removeHealthSummaryRow({
          queryClient,
          patientId: narrativePatientId,
          slice: 'medications',
          id
        })
      );
      linkedWlaIds.forEach((id) =>
        removeHealthSummaryRow({
          queryClient,
          patientId: narrativePatientId,
          slice: 'weightLossAdministration',
          id
        })
      );
      if (!fromOverview) {
        overviewRemove('medications', Array.from(deletedMedIds));
        overviewRemove('weightLossAdministration', Array.from(linkedWlaIds));
      }

      queryClient.invalidateQueries({ queryKey: ['medication', ...deps], exact: true });
      queryClient.invalidateQueries({ queryKey: ['medication', String(narrativePatientId)] });
      queryClient.invalidateQueries({
        queryKey: ['weightLossAdministration', ...deps],
        exact: true
      });
      invalidateHealthSummaryForPatient({ queryClient, patientId: narrativePatientId });
    }
  });

  const onHandleDelete = async () => {
    let clinicalNoteId = fromOverview ? null : clinicalNote?.id;
    if (!fromOverview && !clinicalNote?.id) {
      const cn = await saveNoteRef?.current?.checkPaymentBeforeSaving?.();

      clinicalNoteId = cn?.id;
    }

    const result = await mutateDeleteMedication.mutateAsync({ clinicalNoteId });

    if (result?.code === 0) {
      hideModal();
      setConfirmOpen(false);
    }
  };

  // Only close here — the preview flag drives `disabled` and the modal title, so clearing it now
  // would visibly re-enable the form mid-slide. handleAfterClose does it once the exit is done.
  const handleCloseFromMedicationPreviewFromWeightLoss = () => {
    hideModal();
  };

  const handleSelectMedication = async (medication) => {
    formik.setFieldValue('medication', capitalize(medication?.generic_name));
    if (medication?.product_ndc) {
      formik.setFieldValue('product_ndc', medication?.product_ndc);
    }
    if (medication?.product_id) {
      formik.setFieldValue('product_ndc_id', medication?.product_id);
    }
    if (medication?.route) {
      formik.setFieldValue('route', medication?.route[0]);
    }
    if (medication?.active_ingredients) {
      const ndcStrength = getNdcStrengthAndUnit(medication);
      formik.setFieldValue('strength', ndcStrength?.strength ?? '');
      formik.setFieldValue('unit', ndcStrength?.unit ?? null);
    }
  };

  const checkIfWeNeedTheAdditionalFields = (selectedRoute) => {
    return injectionRoutes.includes(selectedRoute);
  };

  const formBody = (
    <form>
      {showPatientPortalReadOnlyAlert && (
        <Alert
          title="Read-only medication"
          message={PATIENT_PORTAL_MEDICATION_READ_ONLY_MESSAGE}
          color="warning"
          width="full"
          className="!mb-4"
        />
      )}
      {!isPatientPortal && isVeradigmMedication && (
        <Alert
          title="Read-only medication"
          message={VERADIGM_MEDICATION_READ_ONLY_MESSAGE}
          color="warning"
          width="full"
          className="!mb-4"
        />
      )}
      {!isPatientPortal && !isVeradigmMedication && medicationReadOnly && (
        <Alert
          title="Restricted: read-only data access"
          message="Editing this area is currently restricted. Contact your internal systems admin if needed."
          color="warning"
          width="full"
          className="!mb-4"
        />
      )}
      {!isPatientPortal && !isVeradigmMedication && disabled && patientIdProp && (
        <Alert
          title="Restricted: read-only data access"
          message="This is a medication created by the provider, so it can't be modified by you."
          color="warning"
          width="full"
          className="!mb-4"
        />
      )}
      <div>
        <label className="text-sm font-500 text-neutral-800">
          Is this medication currently active or inactive?
        </label>
        <span className="text-danger-500">*</span>

        <div className="!mt-3 flex gap-4">
          <RadioButton
            wrapperClassName="w-max"
            label="Active medication"
            onChange={() => formik.setFieldValue('status', MEDICATION_STATUS.ACTIVE)}
            isSelected={formik.values?.status === MEDICATION_STATUS.ACTIVE}
            disabled={disabled || medicationReadOnly}
          />
          <RadioButton
            wrapperClassName="w-max"
            label="Inactive medication"
            onChange={() => formik.setFieldValue('status', MEDICATION_STATUS.INACTIVE)}
            isSelected={formik.values?.status === MEDICATION_STATUS.INACTIVE}
            disabled={disabled || medicationReadOnly}
          />
        </div>

        {formik.errors?.status && (
          <p className="!pt-2 text-left text-sm text-danger-500">{formik.errors?.status}</p>
        )}
      </div>
      <div className="relative !mt-4 grid grid-cols-[repeat(auto-fit,minmax(200px,1fr))] gap-4">
        <SelectMedicationTable
          medication={formik?.values?.medication}
          handleSelectMedication={handleSelectMedication}
          tableClassName="top-[76px]"
          required
          errorInput={formik?.errors?.medication}
          disabled={disabled || medicationReadOnly}
        />
        <Input
          label="NDC"
          name="product_ndc"
          value={formik?.values?.product_ndc}
          onChange={formik.handleChange}
          disabled={disabled || medicationReadOnly}
        />
      </div>

      <div className="mt-4">
        <Textarea
          isEditor
          label="SIG"
          name="sig"
          placeholder="e.g. Take 1 tablet by mouth twice daily"
          value={formik?.values?.sig}
          onChange={(event) => formik.setFieldValue('sig', event)}
          className="min-h-[150px]"
          disabled={disabled || medicationReadOnly}
        />
      </div>

      <div className="mt-4 grid grid-cols-5 gap-4 md:grid-cols-2 sm:grid-cols-1">
        <div className="col-span-2 md:col-span-1 sm:col-span-1">
          <Input
            label="Strength"
            name="strength"
            value={formik?.values?.strength}
            onChange={(event) => formik.setFieldValue(`strength`, event?.target?.value)}
            units={UNIT_OPTIONS}
            currentUnit={formik?.values?.unit}
            onUnitChange={handleCurrentUnitChange}
            unitPanelClassName="left-0 !z-[9999] h-[300px] overflow-y-scroll"
            error={formik?.errors?.strength || formik?.errors?.unit}
            disabled={disabled || medicationReadOnly}
          />
        </div>
        <Input
          label="Quantity"
          value={formik?.values?.quantity}
          type="number"
          onChange={(event) =>
            formik.setFieldValue(
              'quantity',
              event?.target?.value ? Number(event?.target?.value) : null
            )
          }
          error={formik?.errors?.quantity}
          disabled={disabled || medicationReadOnly}
        />
        <Input
          label="Days supply"
          value={formik?.values?.days_supply}
          type="number"
          onChange={(event) =>
            formik.setFieldValue(
              'days_supply',
              event?.target?.value ? Number(event?.target?.value) : null
            )
          }
          error={formik?.errors?.days_supply}
          disabled={disabled || medicationReadOnly}
        />
        <Input
          label="Refills"
          value={formik?.values?.refills}
          type="number"
          onChange={(event) =>
            formik.setFieldValue(
              'refills',
              event?.target?.value ? Number(event?.target?.value) : null
            )
          }
          error={formik?.errors?.refills}
          disabled={disabled || medicationReadOnly}
        />
      </div>
      <div className="!mt-4 grid grid-cols-[repeat(auto-fit,minmax(200px,1fr))] gap-4">
        <Select
          label="Route"
          isCreatable={true}
          parentClassName="!mt-4"
          onChange={(e) => {
            const selectedRoute = e?.value;
            formik.setFieldValue('route', selectedRoute);
            const shouldShowAdditionalFields = checkIfWeNeedTheAdditionalFields(selectedRoute);
            setShowAdditionalFields(shouldShowAdditionalFields);
          }}
          value={{
            value: formik?.values?.route,
            label: formik?.values?.route
          }}
          options={medicationRouteOptions.map((route) => {
            return {
              value: route,
              label: route
            };
          })}
          placeholder="Select route"
          disabled={disabled || medicationReadOnly}
          isClearable={false}
        />
        {showAdditionalFields && (
          <Input
            className="!mt-4"
            label="Location"
            placeholder="Add location"
            value={formik?.values?.location}
            onChange={(event) => formik.setFieldValue('location', event.target.value)}
            disabled={disabled}
          />
        )}
        {fromWeightLoss && (
          <Input
            label="Weight"
            className="!mt-4"
            placeholder="Add weight"
            value={formik?.values?.weight}
            onChange={(event) => formik.setFieldValue('weight', event.target.value)}
            disabled={disabled}
            rightText="lbs"
          />
        )}

        <Select
          inputId="select-condition"
          label="Condition"
          parentClassName="!mt-4"
          id="condition"
          value={formik?.values?.condition}
          name="condition"
          onChange={(event) => formik?.setFieldValue('condition', event)}
          isCreatable
          isClearable
          isMulti
          components={{ DropdownIndicator: null }}
          placeholder="Type and select condition"
          disabled={disabled || medicationReadOnly}
        />
        <SearchProviders
          label="Prescriber"
          id="prescribingPhysician"
          parentClassName="!mt-4"
          placeholder="Prescriber"
          value={formik?.values?.prescriber}
          onChange={(event) => formik?.setFieldValue('prescriber', event)}
          disabled={disabled || medicationReadOnly}
        />

        <DateInputPicker
          label="Start date"
          parentClassName="!mt-4"
          onChange={(date) => formik.setFieldValue('start_date', date || null)}
          value={formik.values?.start_date}
          error={formik?.errors?.start_date}
          disabled={disabled || medicationReadOnly}
        />
        {formik.values?.status === MEDICATION_STATUS.INACTIVE && (
          <DateInputPicker
            label="End date"
            parentClassName="!mt-4"
            onChange={(date) => formik.setFieldValue('end_date', date || null)}
            value={formik.values?.end_date}
            error={formik?.errors?.end_date}
            disabled={disabled || medicationReadOnly}
          />
        )}
        {patientIdProp && (
          <Select
            label="Practice"
            parentClassName="!mt-4 w-full"
            isClearable={false}
            value={getPrefilledPatientPractice({
              data: practiceOptions,
              formik
            })}
            name="practice_id"
            error={formik?.errors?.practice_id}
            onChange={(event) => formik?.setFieldValue('practice_id', event?.value)}
            options={getPatientPracticeOptions(practiceOptions)}
            placeholder="Select practice"
            required={true}
            disabled={disabled || medicationReadOnly}
          />
        )}
      </div>
      <div className="mt-4">
        <Textarea
          isEditor
          label={
            <div className="flex gap-1">
              <span className="text-sm font-500 text-neutral-800">Note</span>
              <span className="test-sm text-neutral-500">(Optional)</span>
            </div>
          }
          name="note"
          value={formik?.values?.note}
          onChange={(event) => formik.setFieldValue('note', event)}
          className="min-h-[150px]"
          disabled={disabled || medicationReadOnly}
        />
      </div>
    </form>
  );

  if (renderInline) return formBody;

  const effectiveClose = showPreviewMedicationFromWhl
    ? handleCloseFromMedicationPreviewFromWeightLoss
    : hideModal;

  const deleteConfirm = (
    <Confirm
      icon="info-filled"
      iconColor="danger"
      iconSize={42}
      variant="danger"
      title="Delete medication"
      message={`Are you sure you want to delete this medication? ${getMedicationDeleteContextMessage(formik?.values)}`}
      isOpen={!!confirmOpen}
      handleClose={() => setConfirmOpen(false)}
      footerActions={{
        primary: {
          text: 'Delete',
          onClick: onHandleDelete,
          color: 'danger',
          isLoading: mutateDeleteMedication?.isLoading,
          isDisabled: mutateDeleteMedication?.isLoading
        }
      }}
    />
  );

  const isUpserting =
    upsertFormFunctionMutation?.isLoading || upsertWeightLossSyncMutation?.isLoading;

  const deleteButton = !isVeradigmMedication && hasSavedMedication && (
    <Button
      text="Delete"
      icon="trash-mono"
      variant="link"
      color="neutral"
      shade={700}
      iconColor="neutral"
      isDisabled={disabled || mutateDeleteMedication?.isLoading || medicationReadOnly}
      onClick={() => setConfirmOpen(true)}
    />
  );

  const refillButton = onRequestRefill && hasSavedMedication && (
    <Button
      text="Request refill"
      icon="prescribe-mono"
      variant="link"
      color="neutral"
      shade={700}
      iconColor="neutral"
      onClick={() => onRequestRefill(activeShowModal?.data)}
    />
  );

  const footerActions = {
    other: (deleteButton || refillButton) && (
      <div className="flex items-center gap-4">
        {deleteButton}
        {refillButton}
      </div>
    ),
    secondary: { text: isVeradigmMedication ? 'Close' : 'Cancel' },
    primary: !isVeradigmMedication && {
      text: hasExistingMedication ? 'Update' : isNonERXPrescription ? 'Add medication' : 'Create',
      dataQa: 'upsert-btn',
      onClick: () => formik?.submitForm(),
      isLoading: isUpserting,
      isDisabled: isUpserting || disabled || medicationReadOnly
    }
  };

  // Presents through the host Modal's SubModal instead of stacking a second overlay;
  // the delete confirm stacks over the sheet and closing it restores the sheet.
  if (asSubModal && hostSubModal) {
    return (
      <>
        <SubModalRender
          isOpen={!!showModal?.open}
          title={modalTitle}
          onClose={effectiveClose}
          footerActions={footerActions}>
          {formBody}
        </SubModalRender>
        {deleteConfirm}
      </>
    );
  }

  return (
    <Modal
      isDirty={formik.dirty}
      width="medium"
      isOpen={showModal?.open}
      handleClose={effectiveClose}
      onAfterClose={handleAfterClose}
      title={modalTitle}
      isFooter={!diffDosView}
      footerActions={footerActions}>
      {formBody}
      {deleteConfirm}
    </Modal>
  );
};

export default UpsertProviderMedication;
